import { AfterViewInit, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import {CmElement, CmHelp, CmSection, CmTooltip, ContentType} from '../shared/model/cm/managed-content.model';
import { ManagedContentService } from '../shared/service/managed-content.service';
import {EEOI_APP_ROUTES, GlobalConstants} from '../shared/global-constants';
import { UserSessionService } from '../shared/service/user-session.service';
import { DeactivateGuard } from '../deactivate.guard';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { AppInsightsApiService} from "../shared/service/app-insights-api.service";
import { ViewportScroller } from '@angular/common';
import { PagetitleService } from '../shared/service/pagetitle.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { ApplicantType } from '../shared/model/ui/applicant-type';
import { GroupEdp } from '../shared/model/group-edp';
import { FormUtils } from '../shared/helper/form-utils';
import { WindowRef } from '../shared/service/window.service';
import { ModalService } from '../shared/service/modal.service';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';

@Component({
  selector: 'app-applicant-type',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ContinueCancelComponent],
  templateUrl: './applicant-type.component.html',
  styleUrls: ['./applicant-type.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ApplicantTypeComponent implements OnInit, DeactivateGuard, AfterViewInit {
  @Output() cancelEmitter = new EventEmitter<void>();
  
  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  public innerWidth: number = window.innerWidth;
  public applicantToolTipPosition: string = 'top';
  public isMobile: boolean = false;

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);
  public applicantTypes: ApplicantType[] = [];
  public edpGroupData: GroupEdp;

  constructor(private router: Router,
              private cmService: ManagedContentService,
              private userSessionService: UserSessionService,
              private formBuilder: UntypedFormBuilder,
              private appInsightsApiService: AppInsightsApiService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private scroller: ViewportScroller,
              private pagetitleService: PagetitleService,
              private authGuardService: AuthGuardService,
              private modalService: ModalService,
              private windowService: WindowRef,
              private appInsightsLoggerService: AppInsightsLoggerService) { }

  // Custom validator to ensure at least one checkbox is selected
  atLeastOneCheckboxValidator(control: any) {
    const formArray = control;
    const hasAtLeastOne = formArray.controls?.some((ctrl: any) => ctrl.value === true);
    return hasAtLeastOne ? null : { atLeastOne: true };
  }

  public readonly APPLICANT_TYPES = GlobalConstants.APPLICANT_TYPES;
  public readonly pageIllustrationUrl = 'assets/Illustration-Footer-Base-Mountains.svg';

  public currentDate: Date = new Date(Date.now());

  // Creating a Form Group with Form Controls for checkboxes and child count select
  applicantTypeForm = this.formBuilder.group({
    applicantTypes: this.formBuilder.array([], [Validators.required, this.atLeastOneCheckboxValidator]),
    childCount: this.formBuilder.control(1)
  });

  // Array to hold checkbox form controls
  public applicantTypeCheckboxes: boolean[] = [];
  public childCountOptions: string[] = ['1', '2', '3', '4', '5'];
  public showChildCountSelect: boolean = false;

  //Display items
  formSection: CmSection;
  pageTitle: string;
  applicantTypeElement: CmElement;
  applicantTypeElementChild: CmElement;
  hint: string;
  policyError: string;
  appType: string;
  submitted: boolean;
  resetSubmitted: boolean = false;
  public showError: boolean = false;
  applicantToolTip: CmTooltip;
  applicantToolTipText: string;
  applicantToolTipTitle: string;

  // GA4 Custom events
  public submitApplicantType = {
    action: "button_click",
    category: "applicant-type",
    name: "submit",
  }

  public cancelApplicantType = {
    action: "button_click",
    category: "applicant-type",
    name: "cancel",
  }

  ngOnInit(): void {
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }
    
    // Skip applicant-type page when continuing from confirmation
    if (this.userSessionService.data.isContinuingFromConfirmation) {
      // applicantType is already set by startApplicationFor in confirmation component
      // Navigate directly to coverage page
      this.router.navigate(['/coverage']);
      return;
    }

    this.edpGroupData = this.userSessionService.data.edpGroupData;

    for(let prodLine of this.edpGroupData.productsByApplicantType){

      let oneApplicantType = new ApplicantType();
      oneApplicantType.abbreviation = prodLine.applicantType.toLowerCase();
      oneApplicantType.label = FormUtils.capitalizeFirstLetter(prodLine.applicantType);

      this.applicantTypes.push(oneApplicantType);
    }

    // Initialize checkbox controls for each applicant type
    const applicantTypesArray = this.applicantTypeForm.get('applicantTypes') as any;
    this.applicantTypes.forEach(() => {
      applicantTypesArray.push(this.formBuilder.control(false));
    });

    if(this.applicantTypes){
      if(this.applicantTypes.length == 1){
        this.submitted = true;
        // Store as array with single item
        this.userSessionService.data.selectedApplicantTypes = [this.applicantTypes[0].abbreviation];
        this.userSessionService.data.applicantType = this.applicantTypes[0].abbreviation;
        this.router.navigate(['/coverage']);
        this.resetSubmitted = true;
        this.showError = false;
        this.appInsightsApiService.startNavigationEvent([EEOI_APP_ROUTES.COVERAGE].toString());
      }
    }
    
    // Get relevant CM content
    this.formSection = this.cmService.getContentById(ContentType.SECTION, "applicant");

    // Set content
    this.pageTitle = this.formSection.title;
    this.applicantTypeElement = this.cmService.getContentById(ContentType.ELEMENT, "who-is-applying");
    this.applicantTypeElementChild = this.cmService.getContentById(ContentType.ELEMENT, "how-many-children");


    if(this.applicantTypeElement){
      this.applicantToolTip = this.cmService.getContentById(ContentType.TOOLTIP, this.applicantTypeElement.tooltip);
      this.applicantToolTipText = this.applicantToolTip.plainText;
      this.applicantToolTipTitle = this.applicantToolTip.title;
    }

    if(this.pagetitleService){
      this.pagetitleService.setPageTitle('Applicant Type');
    }
    
    this.appInsightsApiService.endNavigationEvent(this.router.url);

    if(this.windowService){
      this.windowService.nativeDocument
        .querySelector('body')
        .addEventListener('click', (event: Event) => this.onClick(event));
    }
    
    setTimeout( ()=>{
      this.onScrollToTop();
    }, 1);

    this.resizeObservable$ = fromEvent(window, 'resize')
    this.resizeSubscription$ = this.resizeObservable$.subscribe( evt => {
      this.innerWidth = window.innerWidth;
      this.changeTooltipPosition();
      this.checkIsMobile();
    });
  }//end of ngOnInit

  ngAfterViewInit(){
    this.innerWidth = window.innerWidth;
    this.changeTooltipPosition();
    this.checkIsMobile();
  }

  changeTooltipPosition(){
    if(this.innerWidth < 497){
      this.applicantToolTipPosition = 'left';
    } else {
      this.applicantToolTipPosition = 'top';
    }
  }

  checkIsMobile(){
    this.innerWidth = window.innerWidth;
      if(this.innerWidth < 768){
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
  }

  onSubmit() {
    this.googleAnalyticsService.sendCustomEvent(this.submitApplicantType);
    if(this.applicantTypeForm.valid) {
      this.submitted = true;
      
      // Get selected applicant types from checkbox array
      const applicantTypesArray = this.applicantTypeForm.get('applicantTypes') as any;
      const selectedTypes: string[] = [];
      
      applicantTypesArray.controls.forEach((control: any, index: number) => {
        if (control.value === true) {
          selectedTypes.push(this.applicantTypes[index].abbreviation);
        }
      });
      
      // Store all selected applicant types in order
      this.userSessionService.data.selectedApplicantTypes = selectedTypes;
      
      // Store first selected applicant type in applicantType field
      this.userSessionService.data.applicantType = selectedTypes[0];
      
      // Store child count if child checkbox is selected
      const hasChild = selectedTypes.some(type => type.toLowerCase() === 'child');
      if (hasChild) {
        this.userSessionService.data.childCount = this.applicantTypeForm.get('childCount')?.value || 1;
        // Set currentChildNumber to 1 for the first child when starting fresh (not continuing from confirmation)
        if (!this.userSessionService.data.isContinuingFromConfirmation && selectedTypes[0].toLowerCase() === 'child') {
          this.userSessionService.data.currentChildNumber = 1;
        }
      }
      
      this.router.navigate(['/coverage']);
      this.resetSubmitted = true;
      this.showError = false;
      this.appInsightsApiService.startNavigationEvent([EEOI_APP_ROUTES.COVERAGE].toString());
    }
    else {
      this.showError = true;
    }
    if(!this.resetSubmitted && this.applicantTypeForm.valid){
      this.submitted = false;
      this.resetSubmitted = false;
    }
  }

  canDeactivate(){
    // Skip cancel modal when continuing to new application
    if (this.userSessionService.data.isContinuingFromConfirmation) {
      return of(true);
    }
    
    if (!this.submitted && !this.userSessionService.data.sessionEnded){
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  onCancel(){
    this.googleAnalyticsService.sendCustomEvent(this.cancelApplicantType);
    this.submitted = false;

    this.cancelEmitter.emit();

    this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from ApplicantType.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      'prism-ui-modal-cancel-modal'
    );

    if (modal && event.target === modal) {
      this.onCancelModal('prism-ui-modal-cancel-modal');
    }
  }

  onScrollToTop(){
    if(this.scroller){
      this.scroller.setOffset([0, 200]);
      this.scroller.scrollToAnchor('topOfPage');
    }
  } 

  // Event Handlers
  logCheckboxChangeEvent($event: Event, index: number) {
    const applicantTypesArray = this.applicantTypeForm.get('applicantTypes') as any;
    
    // Access checked value from custom event detail (prism-ui-checkbox uses custom events)
    const isChecked = ($event as any).detail?.checked ?? false;
    
    // Update the form control value
    applicantTypesArray.at(index).setValue(isChecked);
    
    // Mark as touched and update validity
    applicantTypesArray.markAsTouched();
    applicantTypesArray.updateValueAndValidity();
    
    // Check if 'child' checkbox is selected and update showChildCountSelect
    this.updateChildCountVisibility();
    
    // Update validation error display
    if(applicantTypesArray.invalid){
      this.showError = true;
    } else {
      this.showError = false;
    }
  }

  updateChildCountVisibility() {
    const applicantTypesArray = this.applicantTypeForm.get('applicantTypes') as any;
    this.showChildCountSelect = false;
    
    applicantTypesArray.controls.forEach((control: any, index: number) => {
      if (control.value === true && this.applicantTypes[index]?.abbreviation?.toLowerCase() === 'child') {
        this.showChildCountSelect = true;
      }
    });
  }

  handleChildCountChange($event: Event) {
    // prism-ui-select emits custom event with the selected value in detail
    const customEvent = $event as CustomEvent;
    const selectedValue = customEvent.detail;
    
    if (selectedValue) {
      const parsedValue = parseInt(selectedValue);
      this.applicantTypeForm.get('childCount')?.setValue(parsedValue);
    }
  }

}